const BEGINNING_INDEX_START = -100;
const END_INDEX_START = 100;

export type DefaultProcessStepSection = "beginning" | "end";

export const getSectionStartIndex = (section: DefaultProcessStepSection) =>
  section === "end" ? END_INDEX_START : BEGINNING_INDEX_START;

export const splitDefaultProcessSteps = <T extends { index: number }>(processSteps: T[]) => {
  const beginning = processSteps
    .filter((processStep) => processStep.index < END_INDEX_START)
    .toSorted((a, b) => a.index - b.index);
  const end = processSteps
    .filter((processStep) => processStep.index >= END_INDEX_START)
    .toSorted((a, b) => a.index - b.index);

  return { beginning, end };
};

export const computeNextDefaultProcessStepIndex = (
  processSteps: { index: number }[],
  section: DefaultProcessStepSection,
) => {
  if (processSteps.length === 0) {
    return getSectionStartIndex(section);
  }

  return Math.max(...processSteps.map((processStep) => processStep.index)) + 1;
};

export type DefaultProcessStepSections<T> = Record<DefaultProcessStepSection, T[]>;

export const isDefaultProcessStepSection = (id: string): id is DefaultProcessStepSection =>
  id === "beginning" || id === "end";

export const findDefaultProcessStepSection = <T extends { id: string }>(
  sections: DefaultProcessStepSections<T>,
  id: string,
): DefaultProcessStepSection | null => {
  if (isDefaultProcessStepSection(id)) {
    return id;
  }

  if (sections.beginning.some((processStep) => processStep.id === id)) {
    return "beginning";
  }

  if (sections.end.some((processStep) => processStep.id === id)) {
    return "end";
  }

  return null;
};

const moveItem = <T>(items: T[], fromIndex: number, toIndex: number): T[] => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  if (movedItem === undefined) {
    return items;
  }
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
};

export const moveDefaultProcessStepToSection = <T extends { id: string }>({
  sections,
  activeId,
  overId,
  placeAfter,
}: {
  sections: DefaultProcessStepSections<T>;
  activeId: string;
  overId: string;
  placeAfter: boolean;
}): DefaultProcessStepSections<T> | null => {
  const fromSection = findDefaultProcessStepSection(sections, activeId);
  const toSection = findDefaultProcessStepSection(sections, overId);

  if (!fromSection || !toSection || fromSection === toSection) {
    return null;
  }

  const fromItems = sections[fromSection];
  const toItems = sections[toSection];
  const fromIndex = fromItems.findIndex((processStep) => processStep.id === activeId);
  if (fromIndex === -1) {
    return null;
  }

  const moving = fromItems[fromIndex];
  if (moving === undefined) {
    return null;
  }

  const overIndex = toItems.findIndex((processStep) => processStep.id === overId);
  const insertIndex = overIndex === -1 ? toItems.length : overIndex + (placeAfter ? 1 : 0);

  return {
    ...sections,
    [fromSection]: fromItems.filter((processStep) => processStep.id !== activeId),
    [toSection]: [...toItems.slice(0, insertIndex), moving, ...toItems.slice(insertIndex)],
  };
};

export const reorderDefaultProcessStep = <T extends { id: string }>(
  processSteps: T[],
  activeId: string,
  overId: string,
): T[] | null => {
  const fromIndex = processSteps.findIndex((processStep) => processStep.id === activeId);
  const toIndex = processSteps.findIndex((processStep) => processStep.id === overId);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return null;
  }

  return moveItem(processSteps, fromIndex, toIndex);
};

export const withDefaultProcessStepSectionIndexes = <T extends { index: number }>(
  sections: DefaultProcessStepSections<T>,
): DefaultProcessStepSections<T> => ({
  beginning: sections.beginning.map((processStep, position) => ({
    ...processStep,
    index: BEGINNING_INDEX_START + position,
  })),
  end: sections.end.map((processStep, position) => ({
    ...processStep,
    index: END_INDEX_START + position,
  })),
});
